import { CaretRightIcon, HandCoinsIcon } from '@phosphor-icons/react'
import { type PlainDate, differenceInDays, formatMoney, todayIn } from '@paybae/shared'
import { useNavigate } from 'react-router'

import { Card } from '@/components/Card'
import { PersonDot } from '@/components/PersonAvatar'
import type { IncomeEvent } from '@/lib/api'
import { cn } from '@/lib/cn'
import { haptic } from '@/lib/haptics'
import { useDashboard, useIngresosPorConfirmar } from '@/lib/queries'
import { useSession } from '@/lib/session'

/**
 * «Tu quincena del 14 ya debió caer. ¿Cuánto llegó?»
 *
 * El aviso que abre el momento más importante de la app. Sin él, confirmar un
 * ingreso sería algo que hay que acordarse de hacer, y lo que no se recuerda no
 * se hace: el tablero se quedaría proyectando una quincena que quizá llegó
 * incompleta, o que no llegó.
 *
 * Dos decisiones:
 *
 * - **No reserva espacio mientras carga.** Un esqueleto fijo en el inicio
 *   anunciaría un pendiente que casi nunca existe, y el aviso perdería su
 *   fuerza precisamente el día que sí aparece.
 * - **Calla si falla.** El tablero ya tiene su propio estado de error; un
 *   segundo mensaje rojo debajo no añade nada que hacer.
 *
 * Se monta desde el inicio; este archivo solo exporta el componente.
 */
export function AvisoIngresoPendiente() {
  const navigate = useNavigate()
  const { membership } = useSession()

  // La fecha se toma en la zona horaria del hogar, no en la del teléfono: a las
  // once de la noche en Cancún el teléfono ya dice «mañana» y el aviso de la
  // quincena de hoy desaparecería un día antes de tiempo.
  const hoy = todayIn(membership.household.timezone)
  const pendientes = useIngresosPorConfirmar(hoy)
  // Misma clave que usa el inicio, así que sale de la caché sin una petición más.
  const tablero = useDashboard()

  const lista = pendientes.data ?? []
  if (lista.length === 0) return null

  const integrantes = tablero.data?.members ?? []
  const colorDe = (memberId: string): string | undefined =>
    integrantes.find((m) => m.memberId === memberId)?.personColorToken
  // En un hogar de una sola persona el punto de color no distingue nada.
  const muestraDueno = integrantes.length > 1

  const abrir = (evento: IncomeEvent): void => {
    haptic('light')
    void navigate(`/ingresos/${evento.id}`)
  }

  // Atrasado y «cae hoy» no son lo mismo: uno pide acción y el otro solo avisa.
  //
  // Los dos van en mantequilla, el tinte de «lo que entra», sin borde, como
  // toda caja del papel. Antes el normal iba en índigo, que en esta paleta es
  // el color del dato y no el de un aviso. Lo atrasado se distingue por el
  // filete y el icono en ámbar, no por el fondo: en claro, el tinte mantequilla
  // y el fondo de advertencia son el mismo color (`#fdf3e2`), así que separarlos
  // solo por fondo no habría distinguido nada.
  const atrasado = lista.some((e) => differenceInDays(hoy, e.expectedDate) > 0)
  const tinte = atrasado
    ? 'border-warning/60 bg-tint-butter'
    : 'border-transparent bg-tint-butter'
  const icono = atrasado ? 'bg-warning text-white' : 'bg-accion text-on-accion'

  if (lista.length === 1) {
    const evento = lista[0]!
    return (
      <Card className={cn('p-0', tinte)}>
        <button
          type="button"
          onClick={() => abrir(evento)}
          className="flex w-full items-center gap-3 p-4 text-left active:scale-[0.99]"
        >
          <span className={cn('flex size-10 shrink-0 items-center justify-center rounded-full', icono)}>
            <HandCoinsIcon size={20} weight="fill" />
          </span>

          <span className="min-w-0 flex-1">
            <span className="block font-display text-[16px] font-bold leading-snug tracking-[-0.02em] text-ink">
              {frase(evento, hoy)}
            </span>
            <span className="mt-0.5 block text-[13px] leading-snug text-ink-soft">
              {pregunta(evento, hoy)} {contexto(evento)}
            </span>
          </span>

          <CaretRightIcon size={18} weight="bold" className="shrink-0 text-ink-soft" />
        </button>
      </Card>
    )
  }

  // Con varias pendientes, la frase larga se repetiría línea a línea. El
  // encabezado dice cuántas son y cada fila se queda con lo que la distingue.
  const visibles = lista.slice(0, 3)
  const restantes = lista.length - visibles.length

  return (
    <Card className={cn('flex flex-col gap-1 p-0', tinte)}>
      <header className="flex items-center gap-2.5 px-4 pb-1 pt-4">
        <span className={cn('flex size-9 shrink-0 items-center justify-center rounded-full', icono)}>
          <HandCoinsIcon size={18} weight="fill" />
        </span>
        <h2 className="font-display text-[16px] font-bold tracking-[-0.02em] text-ink">
          Tienes {lista.length} ingresos por confirmar
        </h2>
      </header>

      <ul className="flex flex-col px-2 pb-2">
        {visibles.map((evento) => (
          <li key={evento.id}>
            <button
              type="button"
              onClick={() => abrir(evento)}
              className="flex min-h-[56px] w-full items-center gap-2.5 rounded-card px-2.5 py-2 text-left active:scale-[0.99]"
            >
              {muestraDueno ? <PersonDot token={colorDe(evento.memberId)} /> : null}
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[14px] font-semibold text-ink">
                  {frase(evento, hoy)}
                </span>
                <span className="block truncate text-[12px] text-ink-soft">
                  {pregunta(evento, hoy)} {contexto(evento)}
                </span>
              </span>
              <CaretRightIcon size={16} weight="bold" className="shrink-0 text-ink-soft" />
            </button>
          </li>
        ))}
      </ul>

      {restantes > 0 ? (
        <p className="px-4 pb-4 text-[12px] leading-snug text-ink-soft">
          Y {restantes} más: aparecerán aquí en cuanto confirmes estas.
        </p>
      ) : null}
    </Card>
  )
}

/** «Tu nómina del 14 ya debió caer» · «Hoy cae tu nómina». */
function frase(evento: IncomeEvent, hoy: PlainDate): string {
  const nombre = enMinuscula(evento.sourceName)
  const diasTarde = differenceInDays(hoy, evento.expectedDate)
  if (diasTarde <= 0) return `Hoy cae tu ${nombre}.`
  return `Tu ${nombre} del ${dia(evento.expectedDate, hoy)} ya debió caer.`
}

function pregunta(evento: IncomeEvent, hoy: PlainDate): string {
  return differenceInDays(hoy, evento.expectedDate) <= 0 ? '¿Ya te llegó?' : '¿Cuánto llegó?'
}

/**
 * Lo esperado va como contexto, nunca como propuesta: en un ingreso variable es
 * un cálculo nuestro, y presentarlo como cifra a confirmar invitaría a aceptarlo
 * sin mirar el depósito real.
 */
function contexto(evento: IncomeEvent): string {
  if (evento.kind === 'VARIABLE') return '· ingreso variable'
  return `· esperabas ${formatMoney(evento.expectedAmountCents, { hideZeroCents: true })}`
}

const MESES = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
]

/**
 * El día solo, salvo que sea de otro mes: en octubre, «del 14» a secas puede
 * ser el de este mes o el del pasado, y de eso depende cuánto lleva sin
 * confirmarse.
 */
function dia(fecha: PlainDate, hoy: PlainDate): string {
  const [, mes, d] = fecha.split('-')
  const [, mesHoy] = hoy.split('-')
  const numero = Number(d)
  return mes === mesHoy ? String(numero) : `${numero} de ${MESES[Number(mes) - 1]}`
}

function enMinuscula(nombre: string): string {
  return nombre.charAt(0).toLowerCase() + nombre.slice(1)
}
